/**
 * ItemRegistry - Central registry for all game items
 */

import { Item, ResourceItem, ToolItem, WeaponItem, RangedWeaponItem, ConsumableItem, TorchItem, ArmorItem } from './Item';
import { ItemType, ResourceType } from '../types/enums';

class ItemRegistryClass {
  private items: Map<string, Item> = new Map();

  constructor() {
    this.registerDefaultItems();
  }

  private registerDefaultItems(): void {
    // Resources
    this.register(new ResourceItem({
      id: 'wood',
      name: 'Wood',
      description: 'A piece of wood. Used for crafting.',
      resourceType: ResourceType.Wood,
      value: 1,
      maxStack: 99
    }));

    this.register(new ResourceItem({
      id: 'stone',
      name: 'Stone',
      description: 'A chunk of stone. Used for crafting.',
      resourceType: ResourceType.Stone,
      value: 1,
      maxStack: 99
    }));

    this.register(new ResourceItem({
      id: 'iron_ore',
      name: 'Iron Ore',
      description: 'Raw iron ore. Can be smelted.',
      resourceType: ResourceType.Iron,
      value: 5,
      maxStack: 99
    }));

    this.register(new ResourceItem({
      id: 'fiber',
      name: 'Plant Fiber',
      description: 'Fibrous plant material.',
      resourceType: ResourceType.Fiber,
      value: 1,
      maxStack: 99
    }));

    // Tools
    this.register(new ToolItem({
      id: 'axe_wood',
      name: 'Wooden Axe',
      description: 'A basic axe for chopping trees.',
      toolType: 'axe',
      toolPower: 2,
      durability: 50,
      value: 10
    }));

    this.register(new ToolItem({
      id: 'axe_stone',
      name: 'Stone Axe',
      description: 'An improved axe.',
      toolType: 'axe',
      toolPower: 4,
      durability: 100,
      value: 25
    }));

    this.register(new ToolItem({
      id: 'pickaxe_wood',
      name: 'Wooden Pickaxe',
      description: 'A basic pickaxe for mining.',
      toolType: 'pickaxe',
      toolPower: 2,
      durability: 50,
      value: 10
    }));

    this.register(new ToolItem({
      id: 'pickaxe_stone',
      name: 'Stone Pickaxe',
      description: 'An improved pickaxe.',
      toolType: 'pickaxe',
      toolPower: 4,
      durability: 100,
      value: 25
    }));

    // Sickles - For harvesting plants
    this.register(new ToolItem({
      id: 'sickle_wood',
      name: 'Wooden Sickle',
      description: 'A basic sickle for harvesting plants.',
      toolType: 'sickle',
      toolPower: 2,
      durability: 40,
      value: 8
    }));

    this.register(new ToolItem({
      id: 'sickle_stone',
      name: 'Stone Sickle',
      description: 'An improved sickle for harvesting.',
      toolType: 'sickle',
      toolPower: 4,
      durability: 80,
      value: 20
    }));

    // Jackdrill - Creates Hell Holes
    this.register(new ToolItem({
      id: 'jackdrill',
      name: 'Jackdrill',
      description: 'A powerful drill that can pierce through to other dimensions.',
      toolType: 'drill',
      toolPower: 10,
      durability: 50,
      value: 200
    }));

    // Torch - Light source
    this.register(new TorchItem({
      id: 'torch',
      name: 'Torch',
      description: 'A handheld light source. Press E to toggle.',
      lightRadius: 4,
      lightColor: 'rgba(255, 180, 80, 0.2)',
      durability: 300, // Burns for about 10 minutes (300 seconds at 0.5/sec)
      burnRate: 0.5,
      value: 5
    }));

    // Weapons - Basic tier
    this.register(new WeaponItem({
      id: 'sword_wood',
      name: 'Wooden Sword',
      description: 'A basic wooden sword.',
      damage: 5,
      attackSpeed: 1.5,
      range: 1.5,
      weaponType: 'sword',
      durability: 40,
      value: 15
    }));

    this.register(new WeaponItem({
      id: 'sword_stone',
      name: 'Stone Sword',
      description: 'A stone-bladed sword.',
      damage: 10,
      attackSpeed: 1.2,
      range: 1.5,
      weaponType: 'sword',
      durability: 80,
      value: 35
    }));

    this.register(new WeaponItem({
      id: 'sword_iron',
      name: 'Iron Sword',
      description: 'A proper iron sword.',
      damage: 20,
      attackSpeed: 1.0,
      range: 1.5,
      weaponType: 'sword',
      durability: 150,
      value: 75
    }));

    // Spears - Long reach weapons
    this.register(new WeaponItem({
      id: 'spear_wood',
      name: 'Wooden Spear',
      description: 'A simple spear with long reach.',
      damage: 8,
      attackSpeed: 1.0,
      range: 2.5,
      weaponType: 'spear',
      durability: 35,
      value: 12
    }));

    this.register(new WeaponItem({
      id: 'spear_stone',
      name: 'Stone Spear',
      description: 'A stone-tipped spear with deadly reach.',
      damage: 15,
      attackSpeed: 0.9,
      range: 2.8,
      weaponType: 'spear',
      durability: 70,
      value: 30
    }));

    // Weapons - Advanced tier (enemy drops)
    this.register(new WeaponItem({
      id: 'wolf_fang_dagger',
      name: 'Wolf Fang Dagger',
      description: 'A swift dagger made from wolf fangs.',
      damage: 15,
      attackSpeed: 2.0,
      range: 1.2,
      weaponType: 'dagger',
      durability: 100,
      value: 60
    }));

    this.register(new WeaponItem({
      id: 'bone_club',
      name: 'Bone Club',
      description: 'A heavy club made of bone.',
      damage: 18,
      attackSpeed: 0.8,
      range: 1.8,
      weaponType: 'club',
      durability: 120,
      value: 45
    }));

    this.register(new WeaponItem({
      id: 'orc_cleaver',
      name: 'Orc Cleaver',
      description: 'A brutal cleaver wielded by orcs.',
      damage: 35,
      attackSpeed: 0.7,
      range: 2.0,
      weaponType: 'axe',
      durability: 200,
      value: 120
    }));

    this.register(new WeaponItem({
      id: 'golem_hammer',
      name: 'Golem Hammer',
      description: 'A massive hammer infused with earth magic.',
      damage: 50,
      attackSpeed: 0.5,
      range: 2.5,
      weaponType: 'hammer',
      durability: 300,
      value: 200
    }));

    this.register(new WeaponItem({
      id: 'demon_blade',
      name: 'Demon Blade',
      description: 'A cursed blade burning with hellfire.',
      damage: 45,
      attackSpeed: 1.2,
      range: 2.0,
      weaponType: 'sword',
      durability: 250,
      value: 300
    }));

    this.register(new WeaponItem({
      id: 'spectral_scythe',
      name: 'Spectral Scythe',
      description: 'A ghostly scythe that phases through armor.',
      damage: 30,
      attackSpeed: 1.5,
      range: 2.2,
      weaponType: 'scythe',
      durability: 180,
      value: 150
    }));

    this.register(new WeaponItem({
      id: 'spider_fang',
      name: 'Spider Fang',
      description: 'A venomous spider fang fashioned into a dagger.',
      damage: 12,
      attackSpeed: 2.2,
      range: 1.0,
      weaponType: 'dagger',
      durability: 60,
      value: 40
    }));

    // Consumables
    this.register(new ConsumableItem({
      id: 'apple',
      name: 'Apple',
      description: 'A fresh apple. Restores health.',
      healthRestore: 10,
      staminaRestore: 5,
      value: 5,
      maxStack: 20
    }));

    this.register(new ConsumableItem({
      id: 'cooked_meat',
      name: 'Cooked Meat',
      description: 'Cooked meat. Very nutritious.',
      healthRestore: 25,
      staminaRestore: 15,
      value: 15,
      maxStack: 10
    }));

    this.register(new ConsumableItem({
      id: 'health_potion',
      name: 'Health Potion',
      description: 'Instantly restores health.',
      healthRestore: 50,
      staminaRestore: 0,
      value: 50,
      maxStack: 5
    }));

    // Enemy drop consumables
    this.register(new ConsumableItem({
      id: 'raw_meat',
      name: 'Raw Meat',
      description: 'Raw meat from a slain creature. Cook it first!',
      healthRestore: 5,
      staminaRestore: 10,
      value: 8,
      maxStack: 20
    }));

    this.register(new ConsumableItem({
      id: 'spider_eye',
      name: 'Spider Eye',
      description: 'A gross but potent ingredient.',
      healthRestore: -5,
      staminaRestore: 30,
      value: 15,
      maxStack: 10
    }));

    this.register(new ConsumableItem({
      id: 'demon_heart',
      name: 'Demon Heart',
      description: 'Still beating with dark energy. Fully restores health.',
      healthRestore: 100,
      staminaRestore: 50,
      value: 200,
      maxStack: 3
    }));

    this.register(new ConsumableItem({
      id: 'ectoplasm',
      name: 'Ectoplasm',
      description: 'Ghostly residue. Restores stamina rapidly.',
      healthRestore: 0,
      staminaRestore: 75,
      value: 80,
      maxStack: 5
    }));

    // Special resource drops
    this.register(new ResourceItem({
      id: 'bone',
      name: 'Bone',
      description: 'A bone from a skeleton. Used for crafting.',
      resourceType: ResourceType.Stone,
      value: 5,
      maxStack: 50
    }));

    this.register(new ResourceItem({
      id: 'wolf_pelt',
      name: 'Wolf Pelt',
      description: 'A warm wolf pelt.',
      resourceType: ResourceType.Fiber,
      value: 20,
      maxStack: 20
    }));

    this.register(new ResourceItem({
      id: 'golem_core',
      name: 'Golem Core',
      description: 'A magical core that powered a golem.',
      resourceType: ResourceType.Iron,
      value: 100,
      maxStack: 10
    }));

    // ==========================================
    // CRAFTABLE FROM MONSTER DROPS
    // ==========================================

    // Bone weapons
    this.register(new WeaponItem({
      id: 'bone_knife',
      name: 'Bone Knife',
      description: 'A sharp knife carved from bone. Fast attacks.',
      damage: 10,
      attackSpeed: 2.5,
      range: 1.0,
      weaponType: 'dagger',
      durability: 60,
      value: 25
    }));

    this.register(new WeaponItem({
      id: 'bone_spear',
      name: 'Bone Spear',
      description: 'A spear tipped with sharpened bone.',
      damage: 18,
      attackSpeed: 1.0,
      range: 2.8,
      weaponType: 'spear',
      durability: 80,
      value: 40
    }));

    // Iron tier tools
    this.register(new ToolItem({
      id: 'axe_iron',
      name: 'Iron Axe',
      description: 'A sturdy iron axe. Chops trees quickly.',
      toolType: 'axe',
      toolPower: 6,
      durability: 200,
      value: 60
    }));

    this.register(new ToolItem({
      id: 'pickaxe_iron',
      name: 'Iron Pickaxe',
      description: 'A sturdy iron pickaxe. Mines stone quickly.',
      toolType: 'pickaxe',
      toolPower: 6,
      durability: 200,
      value: 60
    }));

    // Fiber crafts
    this.register(new ConsumableItem({
      id: 'bandage',
      name: 'Bandage',
      description: 'Cloth bandages for treating wounds.',
      healthRestore: 20,
      staminaRestore: 0,
      value: 10,
      maxStack: 20
    }));

    // Wolf pelt crafts
    this.register(new ConsumableItem({
      id: 'fur_cloak',
      name: 'Fur Cloak',
      description: 'A warm cloak that invigorates the wearer.',
      healthRestore: 30,
      staminaRestore: 50,
      value: 50,
      maxStack: 5
    }));

    // Golem core crafts
    this.register(new WeaponItem({
      id: 'golem_sword',
      name: 'Golem Sword',
      description: 'A heavy sword powered by a golem core.',
      damage: 40,
      attackSpeed: 0.8,
      range: 2.0,
      weaponType: 'sword',
      durability: 400,
      value: 250
    }));

    // Spider crafts
    this.register(new ConsumableItem({
      id: 'antidote',
      name: 'Antidote',
      description: 'Cures poison and restores vitality.',
      healthRestore: 15,
      staminaRestore: 40,
      value: 30,
      maxStack: 10
    }));

    // Ectoplasm crafts
    this.register(new ConsumableItem({
      id: 'spirit_essence',
      name: 'Spirit Essence',
      description: 'Concentrated ghost energy. Massive stamina boost.',
      healthRestore: 0,
      staminaRestore: 100,
      value: 100,
      maxStack: 5
    }));

    // ============================================
    // ARMOR & EQUIPMENT
    // ============================================

    // Leather Armor Set (Basic)
    this.register(new ArmorItem({
      id: 'leather_cap',
      name: 'Leather Cap',
      description: 'A simple leather cap offering basic protection.',
      slot: 'head',
      defense: 2,
      durability: 50,
      value: 20
    }));

    this.register(new ArmorItem({
      id: 'leather_tunic',
      name: 'Leather Tunic',
      description: 'A sturdy leather tunic.',
      slot: 'chest',
      defense: 5,
      durability: 60,
      value: 35,
      bonuses: { maxHealth: 10 }
    }));

    this.register(new ArmorItem({
      id: 'leather_pants',
      name: 'Leather Pants',
      description: 'Tough leather leg protection.',
      slot: 'legs',
      defense: 3,
      durability: 55,
      value: 25
    }));

    this.register(new ArmorItem({
      id: 'leather_boots',
      name: 'Leather Boots',
      description: 'Comfortable leather boots.',
      slot: 'feet',
      defense: 2,
      durability: 45,
      value: 20,
      bonuses: { moveSpeed: 0.05 }
    }));

    this.register(new ArmorItem({
      id: 'leather_gloves',
      name: 'Leather Gloves',
      description: 'Protective leather gloves.',
      slot: 'hands',
      defense: 1,
      durability: 40,
      value: 15,
      bonuses: { gatherSpeed: 0.1 }
    }));

    // Iron Armor Set (Mid-tier)
    this.register(new ArmorItem({
      id: 'iron_helm',
      name: 'Iron Helm',
      description: 'A sturdy iron helmet.',
      slot: 'head',
      defense: 5,
      durability: 100,
      value: 50,
      bonuses: { maxHealth: 5 }
    }));

    this.register(new ArmorItem({
      id: 'iron_chestplate',
      name: 'Iron Chestplate',
      description: 'Heavy iron chest protection.',
      slot: 'chest',
      defense: 10,
      durability: 120,
      value: 80,
      bonuses: { maxHealth: 20, defense: 2 }
    }));

    this.register(new ArmorItem({
      id: 'iron_greaves',
      name: 'Iron Greaves',
      description: 'Protective iron leg armor.',
      slot: 'legs',
      defense: 7,
      durability: 100,
      value: 60
    }));

    this.register(new ArmorItem({
      id: 'iron_boots',
      name: 'Iron Boots',
      description: 'Heavy iron boots.',
      slot: 'feet',
      defense: 4,
      durability: 90,
      value: 45
    }));

    // Special/Unique Equipment
    this.register(new ArmorItem({
      id: 'wolf_pelt_cloak',
      name: 'Wolf Pelt Cloak',
      description: 'A warm cloak made from wolf pelts.',
      slot: 'chest',
      defense: 4,
      durability: 80,
      value: 60,
      bonuses: { moveSpeed: 0.1, maxStamina: 20 }
    }));

    this.register(new ArmorItem({
      id: 'golem_gauntlets',
      name: 'Golem Gauntlets',
      description: 'Gauntlets forged from golem cores.',
      slot: 'hands',
      defense: 6,
      durability: 150,
      value: 100,
      bonuses: { attackDamage: 5, gatherSpeed: 0.2 }
    }));

    this.register(new ArmorItem({
      id: 'demon_crown',
      name: 'Demon Crown',
      description: 'A sinister crown pulsing with dark energy.',
      slot: 'head',
      defense: 8,
      durability: 200,
      value: 200,
      bonuses: { attackDamage: 10, maxHealth: 25 }
    }));

    this.register(new ArmorItem({
      id: 'spectral_amulet',
      name: 'Spectral Amulet',
      description: 'An amulet infused with ghostly essence.',
      slot: 'accessory',
      defense: 0,
      durability: 999,
      value: 150,
      bonuses: { maxStamina: 30, moveSpeed: 0.15 }
    }));

    this.register(new ArmorItem({
      id: 'warriors_ring',
      name: "Warrior's Ring",
      description: 'A ring that empowers the wearer in combat.',
      slot: 'accessory',
      defense: 0,
      durability: 999,
      value: 100,
      bonuses: { attackDamage: 8, attackSpeed: 0.1 }
    }));

    // ============================================
    // REGULAR TIER - Cloth Armor (Basic fiber crafts)
    // ============================================
    this.register(new ArmorItem({
      id: 'cloth_hood',
      name: 'Cloth Hood',
      description: 'A simple hood woven from plant fibers.',
      slot: 'head',
      defense: 1,
      durability: 30,
      value: 8
    }));

    this.register(new ArmorItem({
      id: 'cloth_shirt',
      name: 'Cloth Shirt',
      description: 'A basic shirt offering minimal protection.',
      slot: 'chest',
      defense: 2,
      durability: 35,
      value: 12
    }));

    this.register(new ArmorItem({
      id: 'cloth_pants',
      name: 'Cloth Pants',
      description: 'Simple cloth leg coverings.',
      slot: 'legs',
      defense: 1,
      durability: 30,
      value: 10
    }));

    this.register(new ArmorItem({
      id: 'cloth_sandals',
      name: 'Cloth Sandals',
      description: 'Light sandals for nimble movement.',
      slot: 'feet',
      defense: 1,
      durability: 25,
      value: 8,
      bonuses: { moveSpeed: 0.08 }
    }));

    this.register(new ArmorItem({
      id: 'cloth_wraps',
      name: 'Cloth Wraps',
      description: 'Cloth wrappings for the hands.',
      slot: 'hands',
      defense: 1,
      durability: 25,
      value: 6,
      bonuses: { gatherSpeed: 0.05 }
    }));

    // ============================================
    // MAGIC TIER - Bone Armor (Skeleton drops)
    // ============================================
    this.register(new ArmorItem({
      id: 'bone_helm',
      name: 'Bone Helm',
      description: 'A menacing helmet crafted from bones.',
      slot: 'head',
      defense: 4,
      durability: 70,
      value: 40,
      bonuses: { maxHealth: 10 }
    }));

    this.register(new ArmorItem({
      id: 'bone_armor',
      name: 'Bone Armor',
      description: 'Ribcage armor held together by sinew.',
      slot: 'chest',
      defense: 7,
      durability: 80,
      value: 55,
      bonuses: { defense: 2, maxHealth: 15 }
    }));

    this.register(new ArmorItem({
      id: 'bone_greaves',
      name: 'Bone Greaves',
      description: 'Leg guards made from large bones.',
      slot: 'legs',
      defense: 5,
      durability: 70,
      value: 45,
      bonuses: { maxHealth: 5 }
    }));

    this.register(new ArmorItem({
      id: 'bone_boots',
      name: 'Bone Boots',
      description: 'Boots reinforced with bone plates.',
      slot: 'feet',
      defense: 3,
      durability: 60,
      value: 35
    }));

    // ============================================
    // MAGIC TIER - Wolf Armor (Wolf pelt crafts)
    // ============================================
    this.register(new ArmorItem({
      id: 'wolf_hood',
      name: 'Wolf Hood',
      description: 'A hood made from wolf hide with ears intact.',
      slot: 'head',
      defense: 3,
      durability: 65,
      value: 45,
      bonuses: { moveSpeed: 0.05, maxStamina: 10 }
    }));

    this.register(new ArmorItem({
      id: 'wolf_pants',
      name: 'Wolf Leggings',
      description: 'Warm leggings from wolf pelts.',
      slot: 'legs',
      defense: 4,
      durability: 70,
      value: 50,
      bonuses: { moveSpeed: 0.05 }
    }));

    this.register(new ArmorItem({
      id: 'wolf_boots',
      name: 'Wolf Boots',
      description: 'Soft boots for silent movement.',
      slot: 'feet',
      defense: 3,
      durability: 60,
      value: 40,
      bonuses: { moveSpeed: 0.1 }
    }));

    this.register(new ArmorItem({
      id: 'wolf_gloves',
      name: 'Wolf Gloves',
      description: 'Clawed gloves made from wolf hide.',
      slot: 'hands',
      defense: 2,
      durability: 55,
      value: 35,
      bonuses: { attackDamage: 3, attackSpeed: 0.05 }
    }));

    // ============================================
    // EPIC TIER - Golem Armor (Golem core crafts)
    // ============================================
    this.register(new ArmorItem({
      id: 'golem_helm',
      name: 'Golem Helm',
      description: 'A helmet of living stone, infused with earth magic.',
      slot: 'head',
      defense: 12,
      durability: 250,
      value: 180,
      bonuses: { maxHealth: 30, defense: 5 }
    }));

    this.register(new ArmorItem({
      id: 'golem_chestplate',
      name: 'Golem Chestplate',
      description: 'Massive stone armor pulsing with ancient power.',
      slot: 'chest',
      defense: 18,
      durability: 300,
      value: 250,
      bonuses: { maxHealth: 50, defense: 8 }
    }));

    this.register(new ArmorItem({
      id: 'golem_greaves',
      name: 'Golem Greaves',
      description: 'Leg armor of enchanted stone.',
      slot: 'legs',
      defense: 14,
      durability: 280,
      value: 200,
      bonuses: { maxHealth: 25, defense: 3 }
    }));

    this.register(new ArmorItem({
      id: 'golem_boots',
      name: 'Golem Boots',
      description: 'Heavy boots that shake the ground.',
      slot: 'feet',
      defense: 10,
      durability: 250,
      value: 160,
      bonuses: { maxHealth: 20, moveSpeed: -0.1 }
    }));

    // ============================================
    // EPIC TIER - Demon Armor (Demon heart crafts)
    // ============================================
    this.register(new ArmorItem({
      id: 'demon_chestplate',
      name: 'Demon Chestplate',
      description: 'Armor forged in hellfire, radiating malice.',
      slot: 'chest',
      defense: 14,
      durability: 220,
      value: 280,
      bonuses: { attackDamage: 15, maxHealth: 30 }
    }));

    this.register(new ArmorItem({
      id: 'demon_greaves',
      name: 'Demon Greaves',
      description: 'Leg armor that burns with infernal energy.',
      slot: 'legs',
      defense: 10,
      durability: 200,
      value: 220,
      bonuses: { attackDamage: 8, attackSpeed: 0.1 }
    }));

    this.register(new ArmorItem({
      id: 'demon_boots',
      name: 'Demon Boots',
      description: 'Boots that leave smoldering footprints.',
      slot: 'feet',
      defense: 8,
      durability: 180,
      value: 180,
      bonuses: { moveSpeed: 0.15, attackSpeed: 0.05 }
    }));

    this.register(new ArmorItem({
      id: 'demon_gloves',
      name: 'Demon Gauntlets',
      description: 'Gauntlets wreathed in dark flames.',
      slot: 'hands',
      defense: 6,
      durability: 180,
      value: 200,
      bonuses: { attackDamage: 12, attackSpeed: 0.15 }
    }));

    // ============================================
    // EPIC TIER - Spectral Armor (Ectoplasm crafts)
    // ============================================
    this.register(new ArmorItem({
      id: 'spectral_hood',
      name: 'Spectral Hood',
      description: 'A translucent hood shimmering with ghostly energy.',
      slot: 'head',
      defense: 5,
      durability: 150,
      value: 140,
      bonuses: { maxStamina: 25, moveSpeed: 0.1 }
    }));

    this.register(new ArmorItem({
      id: 'spectral_robe',
      name: 'Spectral Robe',
      description: 'A flowing robe that phases through attacks.',
      slot: 'chest',
      defense: 8,
      durability: 180,
      value: 200,
      bonuses: { maxStamina: 40, moveSpeed: 0.15 }
    }));

    this.register(new ArmorItem({
      id: 'spectral_leggings',
      name: 'Spectral Leggings',
      description: 'Ghostly leg wrappings that enhance agility.',
      slot: 'legs',
      defense: 6,
      durability: 160,
      value: 160,
      bonuses: { maxStamina: 20, moveSpeed: 0.1 }
    }));

    this.register(new ArmorItem({
      id: 'spectral_boots',
      name: 'Spectral Boots',
      description: 'Boots that barely touch the ground.',
      slot: 'feet',
      defense: 4,
      durability: 140,
      value: 130,
      bonuses: { moveSpeed: 0.2 }
    }));

    this.register(new ArmorItem({
      id: 'spectral_gloves',
      name: 'Spectral Gloves',
      description: 'Gloves that pass through solid matter.',
      slot: 'hands',
      defense: 3,
      durability: 130,
      value: 120,
      bonuses: { attackSpeed: 0.2, gatherSpeed: 0.15 }
    }));

    // ============================================
    // AMMUNITION
    // ============================================

    // Primitive ammo
    this.register(new ResourceItem({
      id: 'musket_ball',
      name: 'Musket Ball',
      description: 'Lead balls for primitive firearms.',
      resourceType: ResourceType.Iron,
      value: 2,
      maxStack: 50
    }));

    this.register(new ResourceItem({
      id: 'buckshot',
      name: 'Buckshot',
      description: 'Spread ammunition for blunderbuss.',
      resourceType: ResourceType.Iron,
      value: 3,
      maxStack: 30
    }));

    // Modern ammo
    this.register(new ResourceItem({
      id: 'pistol_ammo',
      name: '9mm Rounds',
      description: 'Standard pistol ammunition.',
      resourceType: ResourceType.Iron,
      value: 1,
      maxStack: 100
    }));

    this.register(new ResourceItem({
      id: 'rifle_ammo',
      name: '5.56mm Rounds',
      description: 'High-velocity rifle ammunition.',
      resourceType: ResourceType.Iron,
      value: 2,
      maxStack: 120
    }));

    this.register(new ResourceItem({
      id: 'shotgun_shells',
      name: 'Shotgun Shells',
      description: '12 gauge shotgun shells.',
      resourceType: ResourceType.Iron,
      value: 3,
      maxStack: 40
    }));

    // Futuristic ammo
    this.register(new ResourceItem({
      id: 'energy_cell',
      name: 'Energy Cell',
      description: 'Compact energy cells for laser weapons.',
      resourceType: ResourceType.Iron,
      value: 5,
      maxStack: 80
    }));

    this.register(new ResourceItem({
      id: 'plasma_cell',
      name: 'Plasma Cell',
      description: 'Superheated plasma containment cells.',
      resourceType: ResourceType.Iron,
      value: 8,
      maxStack: 50
    }));

    this.register(new ResourceItem({
      id: 'rail_charge',
      name: 'Rail Charge',
      description: 'Electromagnetic accelerator charges.',
      resourceType: ResourceType.Iron,
      value: 15,
      maxStack: 20
    }));

    // ============================================
    // PRIMITIVE TIER (Level 10-20) - Black Powder Era
    // ============================================

    this.register(new RangedWeaponItem({
      id: 'flintlock_pistol',
      name: 'Flintlock Pistol',
      description: 'A single-shot black powder pistol.',
      damage: 35,
      fireRate: 0.3,
      range: 12,
      accuracy: 0.6,
      projectileSpeed: 15,
      ammoType: 'musket_ball',
      magazineSize: 1,
      reloadTime: 3.0,
      durability: 100,
      weaponType: 'pistol',
      projectileType: 'bullet',
      projectileColor: '#808080',
      tier: 'primitive',
      value: 150
    }));

    this.register(new RangedWeaponItem({
      id: 'musket',
      name: 'Musket',
      description: 'A long-barreled black powder rifle.',
      damage: 60,
      fireRate: 0.2,
      range: 25,
      accuracy: 0.75,
      projectileSpeed: 20,
      ammoType: 'musket_ball',
      magazineSize: 1,
      reloadTime: 4.0,
      durability: 120,
      weaponType: 'rifle',
      projectileType: 'bullet',
      projectileColor: '#808080',
      tier: 'primitive',
      value: 250
    }));

    this.register(new RangedWeaponItem({
      id: 'blunderbuss',
      name: 'Blunderbuss',
      description: 'A wide-barreled scatter gun.',
      damage: 15,
      fireRate: 0.25,
      range: 8,
      accuracy: 0.4,
      projectileSpeed: 12,
      ammoType: 'buckshot',
      magazineSize: 1,
      reloadTime: 3.5,
      durability: 80,
      weaponType: 'shotgun',
      projectileType: 'shell',
      projectileColor: '#808080',
      tier: 'primitive',
      value: 200
    }));

    // ============================================
    // MODERN TIER (Level 20-30) - Contemporary Weapons
    // ============================================

    this.register(new RangedWeaponItem({
      id: 'pistol_9mm',
      name: '9mm Pistol',
      description: 'A reliable semi-automatic handgun.',
      damage: 20,
      fireRate: 3.0,
      range: 15,
      accuracy: 0.8,
      projectileSpeed: 30,
      ammoType: 'pistol_ammo',
      magazineSize: 15,
      reloadTime: 1.5,
      durability: 200,
      weaponType: 'pistol',
      projectileType: 'bullet',
      projectileColor: '#ffcc00',
      tier: 'modern',
      value: 400
    }));

    this.register(new RangedWeaponItem({
      id: 'smg',
      name: 'SMG',
      description: 'Compact submachine gun with high fire rate.',
      damage: 12,
      fireRate: 10.0,
      range: 12,
      accuracy: 0.65,
      projectileSpeed: 28,
      ammoType: 'pistol_ammo',
      magazineSize: 30,
      reloadTime: 2.0,
      durability: 180,
      weaponType: 'smg',
      projectileType: 'bullet',
      projectileColor: '#ffcc00',
      tier: 'modern',
      value: 600
    }));

    this.register(new RangedWeaponItem({
      id: 'assault_rifle',
      name: 'Assault Rifle',
      description: 'Military-grade automatic rifle.',
      damage: 25,
      fireRate: 6.0,
      range: 30,
      accuracy: 0.85,
      projectileSpeed: 35,
      ammoType: 'rifle_ammo',
      magazineSize: 30,
      reloadTime: 2.5,
      durability: 250,
      weaponType: 'rifle',
      projectileType: 'bullet',
      projectileColor: '#ffcc00',
      tier: 'modern',
      value: 800
    }));

    this.register(new RangedWeaponItem({
      id: 'shotgun_pump',
      name: 'Pump Shotgun',
      description: 'Devastating at close range.',
      damage: 40,
      fireRate: 1.0,
      range: 10,
      accuracy: 0.5,
      projectileSpeed: 25,
      ammoType: 'shotgun_shells',
      magazineSize: 8,
      reloadTime: 3.0,
      durability: 200,
      weaponType: 'shotgun',
      projectileType: 'shell',
      projectileColor: '#ff6600',
      tier: 'modern',
      value: 500
    }));

    this.register(new RangedWeaponItem({
      id: 'sniper_rifle',
      name: 'Sniper Rifle',
      description: 'Long-range precision rifle.',
      damage: 100,
      fireRate: 0.5,
      range: 50,
      accuracy: 0.95,
      projectileSpeed: 50,
      ammoType: 'rifle_ammo',
      magazineSize: 5,
      reloadTime: 3.0,
      durability: 200,
      weaponType: 'sniper',
      projectileType: 'bullet',
      projectileColor: '#ffcc00',
      tier: 'modern',
      value: 1000
    }));

    // ============================================
    // FUTURISTIC TIER (Level 30-40) - Energy Weapons
    // ============================================

    this.register(new RangedWeaponItem({
      id: 'laser_pistol',
      name: 'Laser Pistol',
      description: 'Compact energy sidearm.',
      damage: 30,
      fireRate: 4.0,
      range: 20,
      accuracy: 0.9,
      projectileSpeed: 60,
      ammoType: 'energy_cell',
      magazineSize: 20,
      reloadTime: 1.0,
      durability: 300,
      weaponType: 'laser',
      projectileType: 'laser',
      projectileColor: '#ff0000',
      tier: 'futuristic',
      value: 1500
    }));

    this.register(new RangedWeaponItem({
      id: 'laser_rifle',
      name: 'Laser Rifle',
      description: 'Precision energy rifle.',
      damage: 50,
      fireRate: 3.0,
      range: 40,
      accuracy: 0.95,
      projectileSpeed: 70,
      ammoType: 'energy_cell',
      magazineSize: 30,
      reloadTime: 1.5,
      durability: 350,
      weaponType: 'laser',
      projectileType: 'laser',
      projectileColor: '#ff0000',
      tier: 'futuristic',
      value: 2000
    }));

    this.register(new RangedWeaponItem({
      id: 'plasma_rifle',
      name: 'Plasma Rifle',
      description: 'Fires superheated plasma projectiles.',
      damage: 75,
      fireRate: 2.0,
      range: 25,
      accuracy: 0.8,
      projectileSpeed: 40,
      ammoType: 'plasma_cell',
      magazineSize: 15,
      reloadTime: 2.0,
      durability: 280,
      weaponType: 'plasma',
      projectileType: 'plasma',
      projectileColor: '#00ff00',
      tier: 'futuristic',
      value: 2500
    }));

    this.register(new RangedWeaponItem({
      id: 'plasma_cannon',
      name: 'Plasma Cannon',
      description: 'Heavy plasma weapon with explosive impact.',
      damage: 120,
      fireRate: 0.5,
      range: 20,
      accuracy: 0.7,
      projectileSpeed: 30,
      ammoType: 'plasma_cell',
      magazineSize: 5,
      reloadTime: 3.0,
      durability: 250,
      weaponType: 'plasma',
      projectileType: 'plasma',
      projectileColor: '#00ff00',
      tier: 'futuristic',
      value: 3000
    }));

    this.register(new RangedWeaponItem({
      id: 'railgun',
      name: 'Railgun',
      description: 'Electromagnetic accelerator. Devastating power.',
      damage: 200,
      fireRate: 0.3,
      range: 60,
      accuracy: 0.98,
      projectileSpeed: 100,
      ammoType: 'rail_charge',
      magazineSize: 3,
      reloadTime: 4.0,
      durability: 200,
      weaponType: 'railgun',
      projectileType: 'rail',
      projectileColor: '#00ffff',
      tier: 'futuristic',
      value: 5000
    }));

    this.register(new RangedWeaponItem({
      id: 'pulse_smg',
      name: 'Pulse SMG',
      description: 'Rapid-fire energy weapon.',
      damage: 18,
      fireRate: 12.0,
      range: 18,
      accuracy: 0.75,
      projectileSpeed: 50,
      ammoType: 'energy_cell',
      magazineSize: 50,
      reloadTime: 1.8,
      durability: 280,
      weaponType: 'laser',
      projectileType: 'laser',
      projectileColor: '#ff00ff',
      tier: 'futuristic',
      value: 2200
    }));
  }

  register(item: Item): void {
    this.items.set(item.id, item);
  }

  get(id: string): Item | undefined {
    return this.items.get(id);
  }

  getAll(): Item[] {
    return Array.from(this.items.values());
  }

  getByType(type: ItemType): Item[] {
    return this.getAll().filter(item => item.type === type);
  }
}

// Singleton instance
export const ItemRegistry = new ItemRegistryClass();
